<script setup lang="ts">
// One reel drum. Owns the strip DOM + an exposed runAnimation(...) for the parent.
// Styles lifted from the prototype's .reel-drum / .reel-strip / .reel-sym block.
import { ref } from 'vue';
import { SYM_H, animateReel, type ReelStripEntry } from '../lib/spinEngine';

defineProps<{ strip: ReelStripEntry[] }>();

const top = ref(-SYM_H);

defineExpose({
  runAnimation(durationMs: number, landIdx: number, onDone: () => void) {
    return animateReel(durationMs, landIdx, (px) => (top.value = px), onDone);
  },
});
</script>

<template>
  <div class="reel-drum">
    <div class="reel-strip" :style="{ top: `${top}px` }">
      <div v-for="(sym, idx) in strip" :key="idx" class="reel-sym">
        <img v-if="sym.image_url" :src="sym.image_url" :alt="sym.name" />
        <span v-else class="se">{{ sym.emoji }}</span>
        <div class="sl">{{ sym.name }}</div>
      </div>
    </div>
    <div class="wl-top"></div>
    <div class="wl-bot"></div>
    <div class="wl-fill"></div>
    <div class="reel-gloss"></div>
    <div class="rim-bot"></div>
  </div>
</template>

<style scoped>
/* CSS z-index stack (must match CLAUDE.md / prototype):
   2 wl-fill | 5 gloss | 6 ::before darkness | 7 strip | 8 wl-top/bot | 9 ::after, rim-bot */
.reel-drum {
  flex: 1;
  position: relative;
  height: 390px;
  overflow: hidden;
  background: linear-gradient(180deg, #060202 0%, #0f0404 30%, #180606 50%, #0f0404 70%, #060202 100%);
  border-right: 1px solid rgba(120, 60, 0, 0.25);
}
.reel-drum:last-child {
  border-right: none;
}
.reel-drum::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.97) 0%,
    rgba(0, 0, 0, 0.75) 12%,
    rgba(0, 0, 0, 0.2) 28%,
    transparent 38%,
    transparent 62%,
    rgba(0, 0, 0, 0.2) 72%,
    rgba(0, 0, 0, 0.75) 88%,
    rgba(0, 0, 0, 0.97) 100%
  );
  z-index: 6;
  pointer-events: none;
}
.reel-drum::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 4px;
  top: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 180, 60, 0.35) 30%, rgba(255, 220, 100, 0.5) 50%, rgba(255, 180, 60, 0.35) 70%, transparent);
  z-index: 9;
  pointer-events: none;
  box-shadow: 0 1px 6px rgba(255, 140, 0, 0.2);
}
.reel-strip {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  will-change: top;
  z-index: 7;
}
.reel-sym {
  height: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  transition: transform 0.05s;
}
.reel-sym img {
  width: 96px;
  height: 96px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.8));
}
.reel-sym .se {
  font-size: 84px;
  line-height: 1;
  filter: drop-shadow(0 4px 14px rgba(0, 0, 0, 0.9)) drop-shadow(0 0 8px rgba(255, 200, 80, 0.15));
}
.reel-sym .sl {
  font-size: 8px;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  max-width: 70px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}
.wl-top,
.wl-bot {
  position: absolute;
  left: 0;
  right: 0;
  height: 3px;
  z-index: 8;
  pointer-events: none;
  background: rgba(255, 51, 4, 0.9);
  box-shadow: 0 0 10px rgba(255, 51, 4, 1), 0 0 4px rgba(255, 255, 255, 0.4);
}
.wl-top {
  top: 130px;
}
.wl-bot {
  top: 260px;
}
.wl-fill {
  position: absolute;
  left: 0;
  right: 0;
  top: 130px;
  height: 130px;
  background: rgba(255, 51, 4, 0.04);
  z-index: 2;
  pointer-events: none;
}
.reel-gloss {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(255, 255, 255, 0.06) 35%,
    rgba(255, 255, 255, 0.12) 50%,
    rgba(255, 255, 255, 0.06) 65%,
    rgba(0, 0, 0, 0.3) 100%
  );
  z-index: 5;
  pointer-events: none;
}
.rim-bot {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, transparent, rgba(255, 180, 60, 0.35) 30%, rgba(255, 220, 100, 0.5) 50%, rgba(255, 180, 60, 0.35) 70%, transparent);
  z-index: 9;
  pointer-events: none;
}
</style>
